import { useMemo } from 'react';
import { Paper, Typography } from '@wbk/ui';
import { useRouter, useTranslation } from '@wbk/config';
import { CollapsableText, ContentfulMedia } from '@wbk/contentful';
import { TicketDetailsSection } from '@wbk/ticketing';
import SponsorsSection from '../SponsorsSection';
import type { ContentfulEvent, WebSection } from '@wbk/contentful/api';

type Props = {
  event: ContentfulEvent;
};

const EventDetailsRS = ({ event }: Props) => {
  const { t } = useTranslation([
    'contentful_event',
    'contentful_common',
    'common',
    'ticketing_event',
  ]);
  const { locale } = useRouter();
  const isRTL = locale === 'ar';

  const scheduleText = useMemo(() => {
    const open = event?.schedule?.openDateTime;
    if (!open) return null;
    try {
      const start = open ? new Date(open) : null;
      const dateFormatter = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'long',
      });
      const timeFormatter = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
      });
      const datePart = start ? dateFormatter.format(start) : '';
      const timePart = start ? timeFormatter.format(start) : '';
      return `${datePart} · ${timePart}`;
    } catch {
      return null;
    }
  }, [event?.schedule?.openDateTime, locale]);

  return (
    <div className='space-y-6'>
      {/* Row 1: Time & Location */}
      {(scheduleText || event?.location?.title || event?.location?.city) && (
        <div className='grid grid-cols-2 gap-6 empty:hidden'>
          {scheduleText && (
            <Paper className='flex flex-col items-start gap-4 rounded-[14px] border-white/10 !p-3 md:flex-row md:items-center md:!p-6'>
              <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] bg-white/10'>
                <img src='/icons/TimeIcon.svg' alt='' className='h-6 w-6' />
              </div>
              <div className='grow'>
                <Typography variant='label-M' strong>
                  {t('common:starting_from')}
                </Typography>
                <Typography variant='body-M'>
                  {scheduleText || t('contentful_common:na')}
                </Typography>
              </div>
            </Paper>
          )}
          {(event?.location?.title || event?.location?.city) && (
            <Paper className='flex flex-col items-start gap-4 rounded-[14px] border-white/10 !p-3 md:flex-row md:items-center md:!p-6'>
              <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] bg-white/10'>
                <img src='/icons/MapIcon.svg' alt='' className='h-6 w-6' />
              </div>
              <div className='grow'>
                <Typography variant='label-M' strong>
                  {t('contentful_event:location')}
                </Typography>
                <Typography variant='body-M'>
                  <a
                    href={
                      event?.location?.location?.lat && event?.location?.location?.lon
                        ? `https://www.google.com/maps?q=${event.location.location.lat},${event.location.location.lon}`
                        : '#'
                    }
                    target='_blank'
                    rel='noopener noreferrer'
                    className='cursor-pointer underline'
                  >
                    {event?.location?.title || event?.location?.city || t('contentful_common:na')}
                  </a>
                </Typography>
              </div>
            </Paper>
          )}
        </div>
      )}

      {/* Row 2: About */}
      <Paper className='rounded-[14px] border-white/10 !p-3 md:!p-6'>
        <Typography as='h2' variant='heading-S'>
          {t('contentful_event:about')}
        </Typography>
        <div className='mt-3'>
          {event?.description ? (
            <CollapsableText text={event.description} minimum={1} className='text-body-S' />
          ) : (
            <Typography variant='body-S' className='text-text-secondary'>
              {t('contentful_common:na')}
            </Typography>
          )}
        </div>
      </Paper>

      {/* Row 3: Available tickets */}
      <Paper className='rounded-[14px] border-white/10 !p-3 empty:hidden md:!p-6'>
        <TicketDetailsSection slug={event.ticketingUrlSlug || event.slug} />
      </Paper>

      {/* Row 4: Terms & Conditions */}
      {(event?.richTerms || event?.whatToKnow) && (
        <Paper className='space-y-6 rounded-[14px] border-white/10 !p-3 md:!p-6'>
          <Typography as='h2' variant='heading-S'>
            {t('contentful_event:terms_and_conditions')}
          </Typography>
          {event?.richTerms?.json ? (
            <CollapsableText text={event.richTerms} minimum={3} className='text-body-S' />
          ) : event?.whatToKnow ? (
            <CollapsableText text={event.whatToKnow} minimum={3} className='text-body-S' />
          ) : null}
        </Paper>
      )}

      {/* Row 5: Zones */}
      {event?.zone && (
        <div className='rounded-[14px] border-white/10 capitalize'>
          <Typography as='h2' variant='heading-S' className='mb-4 text-white'>
            {t('contentful_event:zones')}
          </Typography>
          <div className='flex items-center gap-6 rounded-[14px] border-white/10 bg-white/10 !p-3 md:!p-6'>
            <ContentfulMedia media={event.zone.zoneLogo} className='h-8 w-8 shrink-0 rounded' />

            <div className='grow'>
              <Typography variant='body-M' strong>
                {event.zone.title}
              </Typography>
              <Typography variant='body-XS' className='text-gray-400'>
                {t('common:explore_more_things')}
              </Typography>
            </div>
            <a
              href={`/${locale}/zones/${event.zone.slug}`}
              className={`transition-colors hover:text-gray-300 ${isRTL ? 'scale-x-[-1]' : ''}`}
            >
              →
            </a>
          </div>
        </div>
      )}

      {/* Row 6: Sponsors */}
      {event?.sponsorsCollection && (
        <SponsorsSection
          section={
            {
              id: 'event-sponsors',
              title: t('common:sponsors'),
              subtitle: t('common:event_sponsors_subtitle'),
              renderType: 'cards-grid',
              contentCollection:
                event.sponsorsCollection as unknown as WebSection['contentCollection'],
              categoriesCollection: { items: [] },
              webLinksCollection: { items: [] },
              tagsCollection: { items: [] },
            } as WebSection
          }
        />
      )}
    </div>
  );
};

export default EventDetailsRS;
